/**
 * Ordnernamen auf der Startseite (7.20).
 *
 * Sascha am 4. September: „das ist kein freies Textfeld“. Bisher tippte man
 * den Namen ein, und aus „Kunden“ und „kunden“ wurden zwei Ordner mit je der
 * Haelfte der Projekte darin. Hier steht, was einen Namen ausmacht und wann
 * zwei dasselbe meinen -- getrennt von der Oberflaeche, weil dieselbe Regel
 * beim Anlegen, beim Umbenennen und auf dem Server gelten muss.
 */
/** Hoechstlaenge, wie in der Spalte. */
export const ORDNER_MAX = 80;
/**
 * Unterordner (S47): der Schrägstrich trennt die Ebenen, „Kunde / Projekt“.
 * Gespeichert wird weiter ein Wort am Projekt -- der Pfad *ist* der Name, und
 * alles, was einen Ordner kannte, kennt damit auch den Unterordner.
 */
export const ORDNER_TRENNER = ' / ';
/**
 * Der Name, wie er gespeichert wird: aussen nichts, innen ein Leerzeichen
 * statt mehrerer. Gross- und Kleinschreibung bleibt, wie sie getippt wurde --
 * „Öhringen“ soll auch so dastehen. Ein Schrägstrich wird immer „ / “, und
 * leere Ebenen fallen weg: „Kunde//Messe/“ ist „Kunde / Messe“.
 */
export function ordnerName(roh) {
    return roh
        .split('/')
        .map((teil) => teil.replace(/\s+/g, ' ').trim())
        .filter(Boolean)
        .join(ORDNER_TRENNER)
        .slice(0, ORDNER_MAX)
        .replace(/[\s/]+$/, '');
}
/** Die Ebenen eines Ordners, von aussen nach innen. */
export function ordnerTeile(name) {
    const n = ordnerName(name);
    return n ? n.split(ORDNER_TRENNER) : [];
}
/** Liegt `pfad` in `ordner` -- er selbst oder darunter? */
export function imOrdner(pfad, ordner) {
    const p = ordnerName(pfad);
    const o = ordnerName(ordner);
    return o !== '' && (p === o || p.startsWith(o + ORDNER_TRENNER));
}
/** Eine Ebene zum Vergleichen: ohne Gross/Klein, ohne Zeichensetzung. */
function teilVergleichbar(teil) {
    return teil.toLocaleLowerCase('de').replace(/[^\p{L}\p{N}]/gu, '');
}
/** Der Name zum Vergleichen, Ebene für Ebene. */
function vergleichbar(name) {
    return ordnerTeile(name).map(teilVergleichbar).join('/');
}
/** Meinen zwei Namen denselben Ordner? „Kunden“ und „kunden “ tun es. */
export function gleicherOrdner(a, b) {
    return vergleichbar(a) === vergleichbar(b) && vergleichbar(a).replace(/\//g, '') !== '';
}
/**
 * Abstand nach Damerau-Levenshtein, abgebrochen, sobald er die Schranke
 * reisst.
 *
 * Damerau und nicht nur Levenshtein wegen des Buchstabendrehers: „Oehringne“
 * statt „Oehringen“ ist der haeufigste Vertipper ueberhaupt, und Levenshtein
 * zaehlt ihn als zwei Schritte (loeschen und einfuegen). Mit der Schranke 1
 * waere er damit durchgerutscht -- der erste Test hat das gefunden.
 */
function abstand(a, b, schranke) {
    if (Math.abs(a.length - b.length) > schranke)
        return schranke + 1;
    // Drei Zeilen: die aktuelle, die vorige und die davor -- die letzte braucht
    // es nur fuer den Dreher.
    let davor = [];
    let vorige = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
        const zeile = [i];
        let kleinstes = i;
        for (let j = 1; j <= b.length; j++) {
            const kosten = a[i - 1] === b[j - 1] ? 0 : 1;
            let wert = Math.min(zeile[j - 1] + 1, vorige[j] + 1, vorige[j - 1] + kosten);
            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
                wert = Math.min(wert, davor[j - 2] + 1);
            }
            zeile.push(wert);
            if (wert < kleinstes)
                kleinstes = wert;
        }
        if (kleinstes > schranke)
            return schranke + 1;
        davor = vorige;
        vorige = zeile;
    }
    return vorige[b.length];
}
/**
 * Der vorhandene Ordner, der gemeint sein duerfte -- oder nichts.
 *
 * Zwei Faelle: derselbe Name anders geschrieben, und ein Vertipper. Ein
 * Zeichen Unterschied reicht bei kurzen Namen schon aus, um daneben zu
 * liegen, deshalb erst ab fuenf Zeichen und hoechstens einer Abweichung --
 * „Kunden“ und „Kunde“ sind vielleicht zwei Ordner, „Öhringen“ und
 * „Öhringne“ ist ein Vertipper. Wer es doch getrennt will, tippt weiter und
 * legt den neuen Ordner an; der Vorschlag fragt, er entscheidet nicht.
 */
export function ordnerVorschlag(neu, vorhandene) {
    const gesucht = vergleichbar(neu);
    if (!gesucht)
        return null;
    for (const kandidat of vorhandene) {
        if (ordnerName(kandidat) === ordnerName(neu))
            return null; // genau der: nichts vorzuschlagen
        if (gleicherOrdner(kandidat, neu))
            return kandidat;
    }
    // Der Vertipper wird je Ebene gesucht: „Kunde / 2025“ und „Kunde / 2026“
    // sind zwei Jahre -- im ganzen Pfad gemessen wären sie lang genug zum
    // Raten und einen Buchstaben auseinander.
    const teile = gesucht.split('/');
    let bester = null;
    for (const kandidat of vorhandene) {
        const andere = vergleichbar(kandidat).split('/');
        if (andere.length !== teile.length)
            continue;
        const anders = teile.flatMap((t, i) => (t === andere[i] ? [] : [i]));
        if (anders.length !== 1)
            continue;
        const t = teile[anders[0]];
        if (t.length < 5)
            continue;
        const d = abstand(t, andere[anders[0]], 1);
        if (d <= 1 && (!bester || d < bester.d))
            bester = { name: kandidat, d };
    }
    return bester?.name ?? null;
}
/** Die Ordner einer Projektliste, mit Anzahl, alphabetisch. */
export function ordnerListe(projekte) {
    const zaehler = new Map();
    for (const p of projekte) {
        const name = ordnerName(p.folder ?? '');
        if (!name)
            continue;
        zaehler.set(name, (zaehler.get(name) ?? 0) + 1);
    }
    return [...zaehler.entries()]
        .map(([name, anzahl]) => ({ name, anzahl }))
        .sort((a, b) => a.name.localeCompare(b.name, 'de'));
}
/**
 * Die Ordner als Baum, flach in Lesereihenfolge: jeder Ordner, dann seine
 * Unterordner, dann der nächste. Ein Ordner, in dem nur Unterordner liegen,
 * steht trotzdem da -- „Kunde“ gibt es, sobald es „Kunde / Messe“ gibt.
 */
export function ordnerBaum(projekte) {
    const knoten = new Map();
    for (const p of projekte) {
        const teile = ordnerTeile(p.folder ?? '');
        for (let i = 0; i < teile.length; i++) {
            const pfad = teile.slice(0, i + 1).join(ORDNER_TRENNER);
            let k = knoten.get(pfad);
            if (!k) {
                k = { pfad, name: teile[i], tiefe: i, projekte: [], gesamt: 0 };
                knoten.set(pfad, k);
            }
            k.gesamt++;
            if (i === teile.length - 1)
                k.projekte.push(p);
        }
    }
    // Ebene für Ebene sortieren, nicht den Pfad als Text: sonst stünde
    // „Kunde A / x“ zwischen „Kunde“ und „Kunde / Messe“.
    const reihe = (k) => k.pfad.split(ORDNER_TRENNER);
    return [...knoten.values()].sort((a, b) => {
        const x = reihe(a);
        const y = reihe(b);
        for (let i = 0; i < Math.min(x.length, y.length); i++) {
            const c = x[i].localeCompare(y[i], 'de');
            if (c !== 0)
                return c;
        }
        return x.length - y.length;
    });
}
/**
 * **Der Name des Projektordners in der Nextcloud** (7.232; Sascha, 6.
 * Oktober: „Ordner Name automatisch in nextcloud: 2026_08_23_Ordner_name“).
 * Vorn das Datum, an dem das Projekt angelegt wurde (deutsche Zeit), dann
 * der Name des Projekts mit Unterstrichen statt Leerzeichen. So stehen die
 * Ordner im Dateimanager nach Datum sortiert.
 *
 * Trägt der Name schon ein Datum vorn („2026-08-23 Messe“), kommt kein
 * zweites davor. Zeichen, die in einem Ordnernamen nicht gehen, werden zu
 * Unterstrichen. Hier und nicht im Server: der Knopf in der Ordnerwahl nennt
 * den Namen, bevor es den Ordner gibt.
 */
export function projektordnerName(projektName, angelegt) {
    const name = projektName.replace(/[\\/:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) || 'Projekt';
    const unterstriche = name.replace(/ /g, '_');
    if (/^\d{4}[-_.]\d{2}[-_.]\d{2}/.test(name))
        return unterstriche;
    const tag = new Date(angelegt);
    const datum = Number.isNaN(tag.getTime())
        ? null
        : new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' }).format(tag);
    return datum ? `${datum.replace(/-/g, '_')}_${unterstriche}` : unterstriche;
}
//# sourceMappingURL=ordner.js.map